import React from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import {
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Psychology as PsychologyIcon,
  Healing as HealingIcon,
  LocalHospital as CrisisIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';

/**
 * AlertBadges Component
 *
 * Displays crisis flags, primary domains, risk level, severity ratings,
 * and conversation alerts as compact badges. Used in both client list view and client detail view.
 *
 * Props:
 * - triage: Triage object from API containing crisisFlags, primaryDomains, riskLevel
 * - alerts: Alerts object from API containing conversation alert counts and priorities
 * - size: 'small' | 'medium' (default: 'small')
 * - maxItems: Maximum number of items to display (default: all)
 * - showRiskLevel: Whether to show the overall risk level badge (default: true)
 */
function AlertBadges({ triage, alerts, size = 'small', maxItems, showRiskLevel = true }) {
  // Show component if either triage is assessed OR there are pending alerts
  const hasTriageData = triage && triage.status === 'assessed';
  const hasPendingAlerts = alerts && alerts.hasPendingAlerts;

  if (!hasTriageData && !hasPendingAlerts) {
    return null;
  }

  const { crisisFlags = [], primaryDomains = [], riskLevel } = triage || {};

  // Combine all items for display
  const allItems = [
    ...crisisFlags.map(flag => ({
      type: 'crisis',
      label: flag.label,
      severity: flag.severity,
      tooltip: `Crisis: ${flag.label}`
    })),
    ...primaryDomains.map(domain => ({
      type: 'domain',
      label: domain.label,
      severity: domain.severity,
      score: domain.score,
      tooltip: `${domain.label}: ${domain.score} (${domain.severity})`
    }))
  ];

  // Limit items if maxItems is specified
  const displayItems = maxItems ? allItems.slice(0, maxItems) : allItems;
  const hasMore = maxItems && allItems.length > maxItems;

  // Get color and icon for severity
  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high':
      case 'severe':
        return 'error';
      case 'medium':
      case 'moderately severe':
      case 'moderate':
        return 'warning';
      default:
        return 'info';
    }
  };

  const getIcon = (item) => {
    if (item.type === 'crisis') {
      return <CrisisIcon fontSize={size} />;
    }
    switch (item.label) {
      case 'Depression':
        return <PsychologyIcon fontSize={size} />;
      case 'Anxiety':
        return <WarningIcon fontSize={size} />;
      case 'Trauma':
        return <HealingIcon fontSize={size} />;
      default:
        return <InfoIcon fontSize={size} />;
    }
  };

  // Get risk level color
  const getRiskLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  if (displayItems.length === 0 && !showRiskLevel && !hasPendingAlerts) {
    return null;
  }

  // Get color for conversation alert priority
  const getAlertPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
      {/* Conversation Alerts Badge - Show First (Most Important) */}
      {hasPendingAlerts && (
        <Tooltip title={`${alerts.pendingCount} pending alert${alerts.pendingCount > 1 ? 's' : ''} - Priority: ${alerts.highestPriority}`}>
          <Chip
            icon={<NotificationsIcon />}
            label={`${alerts.pendingCount} Alert${alerts.pendingCount > 1 ? 's' : ''}`}
            color={getAlertPriorityColor(alerts.highestPriority)}
            size={size}
            sx={{
              fontWeight: 'bold',
              animation: alerts.highestPriority === 'urgent' ? 'pulse 2s infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.7 }
              }
            }}
          />
        </Tooltip>
      )}

      {/* Risk Level Badge */}
      {showRiskLevel && riskLevel && (
        <Tooltip title={`Overall Risk Level: ${riskLevel}`}>
          <Chip
            icon={riskLevel === 'high' ? <ErrorIcon /> : <WarningIcon />}
            label={`${riskLevel} risk`}
            color={getRiskLevelColor(riskLevel)}
            size={size}
            sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}
          />
        </Tooltip>
      )}

      {/* Crisis Flags and Domain Badges */}
      {displayItems.map((item, index) => (
        <Tooltip key={index} title={item.tooltip}>
          <Chip
            icon={getIcon(item)}
            label={item.score ? `${item.label}: ${item.score}` : item.label}
            color={getSeverityColor(item.severity)}
            size={size}
            variant={item.type === 'crisis' ? 'filled' : 'outlined'}
            sx={{
              fontWeight: item.type === 'crisis' ? 'bold' : 'normal',
              borderWidth: item.type === 'crisis' ? 2 : 1
            }}
          />
        </Tooltip>
      ))}

      {/* "More" indicator if items were truncated */}
      {hasMore && (
        <Tooltip title={`+${allItems.length - maxItems} more alerts`}>
          <Chip
            label={`+${allItems.length - maxItems}`}
            size={size}
            variant="outlined"
            sx={{ fontWeight: 'bold' }}
          />
        </Tooltip>
      )}
    </Box>
  );
}

export default AlertBadges;
